import { useMutation } from "@tanstack/react-query";
import { PosterProject } from "PosterEditor/model/poster-project";
import { useAuthContext } from "BEDROCK/auth/auth-provider";

export function useProjectRemoteStore() {
  const { client, userID } = useAuthContext();

  const save = useMutation({
    mutationFn: async (project: PosterProject) => {
      if (!userID || !client)
        throw new Error("UseProjectRemoteStore 11: User not authenticated");
      const { id, name, createdAt, updatedAt, ...jsonRest } = project;
      try {
        const { data, error } = await client.from("projects").upsert(
          [
            {
              id,
              user_id: userID,
              title: name,
              created_at: new Date(createdAt).toISOString(),
              updated_at: new Date(updatedAt).toISOString(),
              thumbnail: project.thumbnail,
              isTemplateDraft: project.isTemplateDraft,
              json: jsonRest,
            },
          ],
          { onConflict: "id" }
        );

        if (error) throw error;

        return data;
      } catch (err) {
        throw err;
      }
    },
  });

  const remove = useMutation({
    mutationFn: async (projectID: string) => {
      if (!userID || !client)
        throw new Error("UseProjectRemoteStore 41: User not authenticated");
      const { data, error } = await client
        .from("projects")
        .delete()
        .eq("id", projectID);
      if (error) throw error;
      return data;
    },
  });

  const fetchAllProjects = async (): Promise<PosterProject[]> => {
    if (!userID || !client)
      throw new Error("UseProjectRemoteStore 52: User not authenticated");
    const { data, error } = await client
      .from("projects")
      .select("*")
      .eq("user_id", userID);

    if (error) throw error;
    const projects: PosterProject[] = data.map((project) => ({
      id: project.id,
      name: project.title,
      createdAt: new Date(project.created_at),
      updatedAt: new Date(project.updated_at),
      thumbnail: project.thumbnail,
      modelVersion: 1,
      isTemplateDraft: false,
      ...project.json,
    }));
    return projects;
  };

  const fetchProject = async (projectID: string): Promise<PosterProject> => {
    if (!userID || !client)
      throw new Error("UseProjectRemoteStore 73: User not authenticated");

    const { data, error } = await client
      .from("projects")
      .select("*")
      .eq("user_id", userID)
      .eq("id", projectID)
      .single();

    if (error) throw error;

    const {
      id,
      title,
      created_at,
      updated_at,
      thumbnail,
      isTemplateDraft,
      json,
    } = data;

    return {
      id,
      name: title,
      createdAt: new Date(created_at),
      updatedAt: new Date(updated_at),
      thumbnail,
      isTemplateDraft,
      modelVersion: 1,
      ...json,
    } as PosterProject;
  };

  return { save, remove, fetchAllProjects, fetchProject };
}
